import type {
  CharacterEntry,
  Lesson,
  LessonBundle,
  Sentence,
  Word,
} from '@ezishizi/content-schema';
import { CHALLENGE, REVIEW_MIX, SESSION } from './config.ts';
import {
  canShowPicture,
  hanziDistractors,
  pictureDistractors,
  soundsAlike,
} from './distractors.ts';
import type { MasteryMap } from './mastery.ts';
import { getScore } from './mastery.ts';
import { syllableInWord, toneChoices, toneOf } from './pinyin.ts';
import { mulberry32, shuffle, type Rng } from './rng.ts';
import { selectReviewCharacters } from './srs.ts';
import type {
  ExerciseType,
  IntroduceShow,
  MasterySkill,
  Module,
  Question,
  TraceMode,
  WordGame,
  ToneDrawMode,
} from './types.ts';

export interface BuildContext {
  bundle: LessonBundle;
  mastery: MasteryMap;
  higherChinese: boolean;
  speechAvailable: boolean;
  /** Seed for deterministic choices; use e.g. `${profileId}:${sessionId}`. */
  seed: string;
  /** Tones (1-4) the child mishears often enough that review should surface them sooner; from
   * the profile's tone stats. Only used by `buildReview`. Omitted (no bias) when unknown. */
  weakTones?: ReadonlySet<number>;
}

export const isWriteTarget = (e: CharacterEntry, higherChinese: boolean) =>
  e.skills.includes('write') || (higherChinese && e.higherWrite);

export function lessonEntries(
  lesson: Lesson,
  higherChinese: boolean,
  setId?: string,
): CharacterEntry[] {
  return lesson.sets
    .filter((s) => !setId || s.id === setId)
    .flatMap((s) => s.entries)
    .filter((e) => higherChinese || !e.higherOnly);
}

export function wordOf(ctx: BuildContext, e: CharacterEntry): Word | null {
  return e.wordId ? (ctx.bundle.words[e.wordId] ?? null) : null;
}

/** Words ordered by closeness: same set, then rest of lesson, then everything in the bundle. */
export function wordPool(ctx: BuildContext, e: CharacterEntry): Word[] {
  const lesson = ctx.bundle.lesson;
  const set = lesson.sets.find((s) => s.entries.some((x) => x.id === e.id));
  const ids = new Set<string>();
  const out: Word[] = [];
  const push = (wid: string | null) => {
    if (!wid || ids.has(wid)) return;
    const w = ctx.bundle.words[wid];
    if (!w) return;
    ids.add(wid);
    out.push(w);
  };
  for (const x of set?.entries ?? []) push(x.wordId);
  for (const s of lesson.sets) for (const x of s.entries) push(x.wordId);
  for (const w of Object.values(ctx.bundle.words)) push(w.id);
  return out;
}

/** The syllable the taught character has inside its teaching word ("yú" for 鱼 in 小鱼). */
export function taughtSyllable(ctx: BuildContext, e: CharacterEntry, word: Word): string | null {
  return syllableInWord(e.char, word, ctx.bundle.characters[e.char]);
}

function exposures(ctx: BuildContext, char: string): number {
  let n = 0;
  for (const m of ctx.mastery.values()) if (m.character === char) n += m.attempts;
  return n;
}

function choiceCount(ctx: BuildContext, char: string, review: boolean): number {
  if (review) return SESSION.choicesReview;
  return exposures(ctx, char) === 0 ? SESSION.choicesFirst : SESSION.choicesLater;
}

export function readSentenceFor(
  ctx: BuildContext,
  word: Word,
): { sentence: Sentence; blankIdx: number } | null {
  for (const sid of word.sentenceIds) {
    const s = ctx.bundle.sentences[sid];
    if (!s || s.mode !== 'read') continue;
    const idx = s.blankableTokenIdx.find((i) => s.tokens[i]?.wordId === word.id);
    if (idx !== undefined) return { sentence: s, blankIdx: idx };
  }
  return null;
}

export function anySentenceFor(ctx: BuildContext, word: Word): Sentence | null {
  for (const sid of word.sentenceIds) {
    const s = ctx.bundle.sentences[sid];
    if (s) return s;
  }
  return null;
}

let counter = 0;
export const qid = (type: ExerciseType, e: CharacterEntry) => `${type}:${e.id}:${++counter}`;

/** Every character in the lesson that the child is expected to know (for distractor pools). */
export function lessonCharSet(ctx: BuildContext): Set<string> {
  return new Set(lessonEntries(ctx.bundle.lesson, ctx.higherChinese).map((x) => x.char));
}

export interface MakeOptions {
  review?: boolean;
  trace?: TraceMode;
  /** Exact number of choices, instead of ramping by how often the character has been seen.
   * Word games ramp within one session, before any mastery is saved. */
  choices?: number;
  /** find_all: how many tiles in the grid and how many of them are the target. */
  grid?: { size: number; targets: number; prompt?: 'hanzi' | 'hidden' | 'pinyin' };
  /** audio_to_picture with fixedChoices: tiles shown as hanzi rather than pictures. */
  hanziIds?: string[];
  /** tone_draw: arrows, draw over the shown contour, or draw blind. */
  toneMode?: ToneDrawMode;
  /** match_pairs: how many pairs on the board (default 3). */
  pairs?: number;
  /** listen_catch: how many words play and how many of them are the target. */
  catch?: { length: number; hits: number };
  /** introduce: which dimension of the word the card shows on its own. */
  show?: IntroduceShow;
  /** audio_to_picture: use exactly these tiles in this order (the trio Listen round). */
  fixedChoices?: Word[];
  /** stroke_missing: which stroke to leave out (else seeded, never the first). */
  strokeIdx?: number;
}

export const WORD_GAME_DEFAULTS = {
  grid: { size: 9, targets: 3 },
  catch: { length: 8, hits: 3 },
} as const;

export function makeQuestion(
  ctx: BuildContext,
  rng: Rng,
  type: ExerciseType,
  e: CharacterEntry,
  opts: MakeOptions = {},
): Question | null {
  const word = wordOf(ctx, e);
  if (!word) return null;
  const n = opts.choices ?? choiceCount(ctx, e.char, opts.review ?? false);
  const base = { entry: e, word };
  switch (type) {
    case 'introduce':
      return {
        ...base,
        id: qid(type, e),
        type,
        skill: 'meaning',
        sentence: anySentenceFor(ctx, word),
        ...(opts.show ? { show: opts.show } : {}),
      };
    case 'audio_to_picture':
    case 'hanzi_to_picture': {
      if (!canShowPicture(word)) return null;
      if (type === 'audio_to_picture' && opts.fixedChoices) {
        if (!opts.fixedChoices.some((w) => w.id === word.id)) return null;
        return {
          ...base,
          id: qid(type, e),
          type,
          skill: 'listen',
          choices: opts.fixedChoices,
          answerId: word.id,
          fixedOrder: true,
          ...(opts.hanziIds ? { hanziIds: opts.hanziIds } : {}),
        };
      }
      const d = pictureDistractors(word, wordPool(ctx, e), n - 1, rng, {
        avoidSyllable: taughtSyllable(ctx, e, word),
      });
      if (d.length === 0) return null;
      const choices = shuffle([word, ...d], rng);
      // audio_to_picture is listening comprehension; hanzi_to_picture is reading for meaning
      // (the child already reads the character, then shows they know what it means).
      const skill: MasterySkill = type === 'audio_to_picture' ? 'listen' : 'meaning';
      return { ...base, id: qid(type, e), type, skill, choices, answerId: word.id };
    }
    case 'picture_to_hanzi': {
      if (!canShowPicture(word)) return null;
      const d = pictureDistractors(word, wordPool(ctx, e), n - 1, rng, {
        avoidSyllable: taughtSyllable(ctx, e, word),
      });
      if (d.length === 0) return null;
      return {
        ...base,
        id: qid(type, e),
        type,
        skill: 'read',
        choices: shuffle([word, ...d], rng),
        answerId: word.id,
      };
    }
    case 'audio_to_hanzi': {
      // Answered by ear alone, so nothing that sounds like the target in any tone is offered
      // (木 is never heard beside 母 or 目); the Pinyin exercises keep those contrasts.
      const target = ctx.bundle.characters[e.char];
      if (!target) return null;
      const d = hanziDistractors(target, Object.values(ctx.bundle.characters), n - 1, rng, {
        sameLesson: lessonCharSet(ctx),
        toneContrast: false,
      });
      if (d.length === 0) return null;
      return {
        ...base,
        id: qid(type, e),
        type,
        skill: 'read',
        choices: shuffle([e.char, ...d], rng),
        answer: e.char,
      };
    }
    case 'pinyin_to_hanzi': {
      // The school test this mirrors: "mù mǎ — which 木?" Distractors lean on near-sounding
      // characters (the same syllable in another tone, 母 for 木), which hanziDistractors ranks
      // first; an exact homophone (目) is never offered, since the pinyin alone cannot tell them apart.
      const target = ctx.bundle.characters[e.char];
      if (!target) return null;
      const chars = [...word.text];
      const syllables = word.pinyin.trim().split(/\s+/);
      const targetIdx = chars.length === syllables.length ? chars.indexOf(e.char) : -1;
      const syllable = syllableInWord(e.char, word, target);
      if (!syllable) return null;
      const d = hanziDistractors(
        target,
        Object.values(ctx.bundle.characters),
        Math.max(n, 3) - 1,
        rng,
        { sameLesson: lessonCharSet(ctx) },
      );
      if (d.length === 0) return null;
      return {
        ...base,
        id: qid(type, e),
        type,
        skill: 'pinyin',
        syllables: targetIdx >= 0 ? syllables : [syllable],
        targetIdx: targetIdx >= 0 ? targetIdx : 0,
        choices: shuffle([e.char, ...d], rng),
        answer: e.char,
      };
    }
    case 'tone': {
      const syllable = syllableInWord(e.char, word, ctx.bundle.characters[e.char]);
      if (!syllable) return null;
      // Only worth asking when the tone marks actually differ: a syllable withTone can't mark
      // (no vowel, broken pinyin) collapses to duplicate choices, so skip it.
      const choices = toneChoices(syllable, rng);
      if (new Set(choices).size !== choices.length || choices.length < 2) return null;
      return { ...base, id: qid(type, e), type, skill: 'pinyin', choices, answer: syllable };
    }
    case 'speak':
      if (!ctx.speechAvailable) return null;
      return { ...base, id: qid(type, e), type, skill: 'speak' };
    case 'trace': {
      if (!isWriteTarget(e, ctx.higherChinese)) return null;
      const mode = opts.trace ?? 'guided';
      return {
        ...base,
        id: qid(type, e),
        type,
        skill: mode === 'memory' || mode === 'flash' ? 'write_memory' : 'write_guided',
        mode,
      };
    }
    case 'stroke_missing': {
      if (!isWriteTarget(e, ctx.higherChinese)) return null;
      const strokes = ctx.bundle.characters[e.char]?.strokeCount ?? 0;
      // Too easy below five strokes (一, 二, 人): the missing stroke is the whole character.
      if (strokes < 5) return null;
      // From the middle of the order, never the first or the last stroke.
      const strokeIdx = opts.strokeIdx ?? 1 + Math.floor(rng() * (strokes - 2));
      const mode = opts.trace === 'memory' ? 'memory' : 'guided';
      return { ...base, id: qid(type, e), type, skill: 'write_guided', strokeIdx, mode };
    }
    case 'tone_draw': {
      const syllable = syllableInWord(e.char, word, ctx.bundle.characters[e.char]);
      if (!syllable) return null;
      const tone = toneOf(syllable);
      if (tone === 0) return null;
      return {
        ...base,
        id: qid(type, e),
        type,
        skill: 'pinyin',
        syllable,
        tone,
        mode: opts.toneMode ?? 'arrows',
      };
    }
    case 'stroke_order': {
      if (!isWriteTarget(e, ctx.higherChinese)) return null;
      const strokeCount = ctx.bundle.characters[e.char]?.strokeCount ?? 0;
      // Fewer than three strokes is trivial, more than eight a wall of tiles.
      if (strokeCount < 3 || strokeCount > 8) return null;
      return { ...base, id: qid(type, e), type, skill: 'write_guided', strokeCount };
    }
    // The multi-word games are built by games.ts, which has the whole set to draw on.
    case 'introduce_trio':
    case 'listen_forks':
    case 'listen_feed':
    case 'listen_scene':
    case 'listen_build':
    case 'listen_bubbles':
    case 'listen_sort':
      return null;
    case 'sentence_fill': {
      const found = readSentenceFor(ctx, word);
      if (!found) return null;
      const pool = wordPool(ctx, e).filter((w) => w.id !== word.id && w.text !== word.text);
      const d = shuffle(pool, rng).slice(0, n - 1);
      if (d.length === 0) return null;
      return {
        ...base,
        id: qid(type, e),
        type,
        skill: 'sentence',
        sentence: found.sentence,
        blankIdx: found.blankIdx,
        choices: shuffle([word, ...d], rng),
        answerId: word.id,
      };
    }
    case 'find_all': {
      // Look-alikes come from the same ranking as audio_to_hanzi (same sound in another tone,
      // shared radical, similar stroke count, same lesson); true homophones are still excluded
      // so the child can find the target by its sound as well as its shape.
      const target = ctx.bundle.characters[e.char];
      if (!target) return null;
      const { size, targets } = opts.grid ?? WORD_GAME_DEFAULTS.grid;
      const d = hanziDistractors(
        target,
        Object.values(ctx.bundle.characters),
        size - targets,
        rng,
        { sameLesson: lessonCharSet(ctx) },
      );
      if (d.length < 2) return null;
      const tiles = shuffle([...Array.from({ length: targets }, () => e.char), ...d], rng);
      const prompt = opts.grid?.prompt ?? 'hanzi';
      return {
        ...base,
        id: qid(type, e),
        type,
        skill: 'read',
        tiles,
        answer: e.char,
        targetCount: targets,
        prompt,
        pinyin: prompt === 'pinyin' ? taughtSyllable(ctx, e, word) : null,
      };
    }
    case 'listen_catch': {
      const { length, hits } = opts.catch ?? WORD_GAME_DEFAULTS.catch;
      const syllable = taughtSyllable(ctx, e, word);
      const others = wordPool(ctx, e)
        .filter((w) => w.id !== word.id && w.text !== word.text)
        .filter((w) => !soundsAlike(word, w, syllable))
        .slice(0, 4);
      if (others.length < 2 || hits >= length) return null;
      // Target positions: never the first slot (the child needs one word to settle in) and never
      // two in a row, so the tap has to follow the ear rather than a rhythm.
      const slots = shuffle(
        Array.from({ length: length - 1 }, (_, i) => i + 1),
        rng,
      );
      const chosen = new Set<number>();
      for (const i of slots) {
        if (chosen.size >= hits) break;
        if (chosen.has(i - 1) || chosen.has(i + 1)) continue;
        chosen.add(i);
      }
      for (const i of slots) {
        if (chosen.size >= hits) break;
        chosen.add(i);
      }
      const fillers = shuffle(others, rng);
      let k = 0;
      const sequence: Word[] = [];
      for (let i = 0; i < length; i++) {
        if (chosen.has(i)) sequence.push(word);
        else sequence.push(fillers[k++ % fillers.length]!);
      }
      return { ...base, id: qid(type, e), type, skill: 'listen', sequence, answerId: word.id };
    }
    case 'match_pairs': {
      if (!canShowPicture(word)) return null;
      const want = Math.max(2, (opts.pairs ?? 3) - 1);
      const d = pictureDistractors(word, wordPool(ctx, e), want, rng, {
        avoidSyllable: taughtSyllable(ctx, e, word),
      });
      if (d.length < 2) return null;
      const pairs = shuffle([word, ...d], rng);
      return {
        ...base,
        id: qid(type, e),
        type,
        skill: 'meaning',
        pairs,
        hanziOrder: shuffle(
          pairs.map((p) => p.id),
          rng,
        ),
      };
    }
    // P3+ reading practice questions are built by the practice player from authored
    // Passage/PracticeQuestion content, never from a single CharacterEntry through this
    // per-character question builder.
    case 'practice_mcq':
    case 'practice_cloze':
    case 'practice_ordering':
    case 'practice_listening_mcq':
    case 'practice_read_aloud':
      return null;
  }
}

/** Fallback order when a type is not applicable to an entry. */
const FALLBACK: Record<ExerciseType, ExerciseType[]> = {
  introduce: [],
  audio_to_picture: ['audio_to_hanzi'],
  hanzi_to_picture: ['audio_to_hanzi'],
  picture_to_hanzi: ['audio_to_hanzi'],
  audio_to_hanzi: [],
  pinyin_to_hanzi: ['audio_to_hanzi'],
  tone: ['pinyin_to_hanzi'],
  speak: ['audio_to_picture', 'audio_to_hanzi'],
  trace: ['picture_to_hanzi', 'audio_to_hanzi'],
  sentence_fill: ['hanzi_to_picture', 'audio_to_hanzi'],
  find_all: ['audio_to_hanzi'],
  listen_catch: ['audio_to_picture', 'audio_to_hanzi'],
  match_pairs: ['hanzi_to_picture', 'audio_to_hanzi'],
  introduce_trio: [],
  listen_forks: ['audio_to_picture'],
  listen_feed: ['audio_to_picture'],
  listen_scene: ['sentence_fill'],
  listen_build: ['audio_to_picture'],
  listen_bubbles: ['audio_to_picture'],
  listen_sort: ['tone'],
  stroke_missing: ['trace'],
  stroke_order: ['trace'],
  tone_draw: ['tone'],
  // Never built by this per-character composer (see `makeQuestion`), so no fallback applies.
  practice_mcq: [],
  practice_cloze: [],
  practice_ordering: [],
  practice_listening_mcq: [],
  practice_read_aloud: [],
};

export function makeWithFallback(
  ctx: BuildContext,
  rng: Rng,
  type: ExerciseType,
  e: CharacterEntry,
  opts: MakeOptions = {},
): Question | null {
  for (const t of [type, ...FALLBACK[type]]) {
    const q = makeQuestion(ctx, rng, t, e, opts);
    if (q) return q;
  }
  return null;
}

export const compact = (qs: Array<Question | null>): Question[] =>
  qs.filter((q): q is Question => q !== null);

/**
 * Run each builder over every entry as its own shuffled round, so consecutive questions are
 * about different characters (a run of five questions on 口 lets the child answer without
 * reading). Round boundaries are patched so the same character never appears twice in a row.
 */
function rounds(
  entries: CharacterEntry[],
  rng: Rng,
  builders: Array<(e: CharacterEntry) => Question | null>,
): Question[] {
  const out: Question[] = [];
  for (const build of builders) {
    let round = compact(shuffle(entries, rng).map(build));
    const last = out[out.length - 1];
    if (last && round.length > 1 && round[0]!.entry.char === last.entry.char) {
      round = [...round.slice(1), round[0]!];
    }
    out.push(...round);
  }
  return out;
}

/** Meet each word (introduce + one easy check), in the authored order so it stays gentle. */
export function buildLearn(ctx: BuildContext, entries: CharacterEntry[]): Question[] {
  const rng = mulberry32(`${ctx.seed}:learn`);
  const met = entries.flatMap((e) =>
    compact([
      makeQuestion(ctx, rng, 'introduce', e),
      makeWithFallback(ctx, rng, 'audio_to_picture', e),
    ]),
  );
  // Close with one Match pairs on the set: the words just met, side by side with their pictures.
  return [...met, ...matchPairsRound(ctx, rng, entries, 1)];
}

/**
 * Up to `count` Match pairs boards, each anchored on a different word of the set (the other
 * two pictures come from the set too when it has them). Words without a picture are skipped.
 */
function matchPairsRound(
  ctx: BuildContext,
  rng: Rng,
  entries: CharacterEntry[],
  count: number,
): Question[] {
  const out: Question[] = [];
  for (const e of shuffle(entries, rng)) {
    if (out.length >= count) break;
    const q = makeQuestion(ctx, rng, 'match_pairs', e);
    if (q) out.push(q);
  }
  return out;
}

export function buildListen(ctx: BuildContext, entries: CharacterEntry[]): Question[] {
  const rng = mulberry32(`${ctx.seed}:listen`);
  return rounds(entries, rng, [
    (e) => makeWithFallback(ctx, rng, 'audio_to_picture', e),
    (e) => makeQuestion(ctx, rng, 'audio_to_hanzi', e),
  ]);
}

export function buildSpeak(ctx: BuildContext, entries: CharacterEntry[]): Question[] {
  const rng = mulberry32(`${ctx.seed}:speak`);
  return compact(entries.map((e) => makeQuestion(ctx, rng, 'speak', e)));
}

export function buildRead(ctx: BuildContext, entries: CharacterEntry[]): Question[] {
  const rng = mulberry32(`${ctx.seed}:read`);
  const drill = rounds(entries, rng, [
    (e) => makeWithFallback(ctx, rng, 'picture_to_hanzi', e),
    (e) => makeWithFallback(ctx, rng, 'sentence_fill', e),
  ]);
  return [...drill, ...matchPairsRound(ctx, rng, entries, 1)];
}

export function buildPinyin(ctx: BuildContext, entries: CharacterEntry[]): Question[] {
  const rng = mulberry32(`${ctx.seed}:pinyin`);
  return rounds(entries, rng, [
    (e) => makeWithFallback(ctx, rng, 'tone', e),
    (e) => makeWithFallback(ctx, rng, 'pinyin_to_hanzi', e),
  ]);
}

/** Trace with the outline, then write from memory: the second round is what the exam asks. */
export function buildWrite(ctx: BuildContext, entries: CharacterEntry[]): Question[] {
  const rng = mulberry32(`${ctx.seed}:write`);
  const targets = entries.filter((e) => isWriteTarget(e, ctx.higherChinese));
  return rounds(targets, rng, [
    (e) => makeQuestion(ctx, rng, 'trace', e, { trace: 'guided' }),
    (e) => makeQuestion(ctx, rng, 'trace', e, { trace: 'memory' }),
  ]);
}

// Static fallback for weighting entries in composeMixed. `trace`'s actual skill depends on the
// mode being built (guided/faded vs memory), so it's resolved by skillForType below instead;
// this default is never read for `trace` but keeps the map total over ExerciseType.
const SKILL_FOR: Record<ExerciseType, MasterySkill> = {
  introduce: 'meaning',
  audio_to_picture: 'listen',
  hanzi_to_picture: 'meaning',
  picture_to_hanzi: 'read',
  audio_to_hanzi: 'read',
  pinyin_to_hanzi: 'pinyin',
  tone: 'pinyin',
  speak: 'speak',
  trace: 'write_memory',
  sentence_fill: 'sentence',
  find_all: 'read',
  listen_catch: 'listen',
  match_pairs: 'meaning',
  introduce_trio: 'meaning',
  listen_forks: 'listen',
  listen_feed: 'listen',
  listen_scene: 'sentence',
  listen_build: 'sentence',
  listen_bubbles: 'listen',
  listen_sort: 'pinyin',
  stroke_missing: 'write_guided',
  stroke_order: 'write_guided',
  tone_draw: 'pinyin',
  // Reading practice is scored by its own session, not through this per-character map; kept
  // total over ExerciseType with the closest existing skill (never actually read for these).
  practice_mcq: 'read',
  practice_cloze: 'read',
  practice_ordering: 'read',
  practice_listening_mcq: 'listen',
  practice_read_aloud: 'speak',
};

/** Mirrors the skill makeQuestion records for a trace at the given mode (see its 'trace' case). */
function skillForType(type: ExerciseType, opts: { trace?: TraceMode } = {}): MasterySkill {
  if (type === 'trace')
    return opts.trace === 'memory' || opts.trace === 'flash' ? 'write_memory' : 'write_guided';
  return SKILL_FOR[type];
}

/**
 * Mixed set following REVIEW_MIX, each slot given to the entry weakest on that skill
 * (with jitter so repeats vary), cycling so every entry gets a turn.
 */
export function composeMixed(
  ctx: BuildContext,
  entries: CharacterEntry[],
  count: number,
  seedTag: string,
  review: boolean,
): Question[] {
  if (entries.length === 0) return [];
  const rng = mulberry32(`${ctx.seed}:${seedTag}`);
  const recipe: ExerciseType[] = [];
  for (const [type, k] of Object.entries(REVIEW_MIX) as Array<[ExerciseType, number]>)
    for (let i = 0; i < k; i++) recipe.push(type);
  const types = shuffle(recipe, rng);
  const used = new Map<string, number>();
  const traceMode: TraceMode = review ? 'memory' : 'faded';
  const out: Question[] = [];
  for (let i = 0; out.length < count && i < count * 3; i++) {
    const type = types[i % types.length]!;
    const skill = skillForType(type, { trace: traceMode });
    const weight = (e: CharacterEntry) =>
      getScore(ctx.mastery, e.char, skill) + (used.get(e.id) ?? 0) * 25 + rng() * 10;
    const ranked = entries.map((e) => ({ e, w: weight(e) })).sort((a, b) => a.w - b.w);
    for (const { e } of ranked) {
      const q = makeWithFallback(ctx, rng, type, e, { review, trace: traceMode });
      if (!q) continue;
      out.push(q);
      used.set(e.id, (used.get(e.id) ?? 0) + 1);
      break;
    }
  }
  return out;
}

export function buildPractice(ctx: BuildContext, entries: CharacterEntry[]): Question[] {
  return composeMixed(ctx, entries, SESSION.practiceQuestions, 'practice', false);
}

/** Question types a test may draw on. Speak and introduce are teaching steps, not test items. */
const TEST_TYPES: ExerciseType[] = [
  'audio_to_picture',
  'picture_to_hanzi',
  'audio_to_hanzi',
  'pinyin_to_hanzi',
  'tone',
  'sentence_fill',
  'trace',
];

/**
 * A simulated test: every character gets `perChar` questions of different, randomly chosen
 * types, then the whole thing is shuffled so the character changes on every question. Four
 * choices, no fallback to easier forms where the type simply doesn't apply (the slot is
 * refilled with another type), tracing from memory.
 */
export function buildTest(
  ctx: BuildContext,
  entries: CharacterEntry[],
  perChar: number,
  seedTag = 'test',
): Question[] {
  if (entries.length === 0) return [];
  const rng = mulberry32(`${ctx.seed}:${seedTag}`);
  const all: Question[] = [];
  for (const e of entries) {
    const picked: Question[] = [];
    for (const type of shuffle(TEST_TYPES, rng)) {
      if (picked.length >= perChar) break;
      const q = makeQuestion(ctx, rng, type, e, { review: true, trace: 'memory' });
      if (q) picked.push(q);
    }
    all.push(...picked);
  }
  return spreadChars(shuffle(all, rng));
}

/**
 * Walk a shuffled list and push any question that repeats the previous character to the first
 * later slot where it doesn't, so the character changes on every question.
 */
export function spreadChars(out: Question[]): Question[] {
  for (let i = 1; i < out.length; i++) {
    if (out[i]!.entry.char !== out[i - 1]!.entry.char) continue;
    const j = out.findIndex(
      (q, k) =>
        k > i &&
        q.entry.char !== out[i - 1]!.entry.char &&
        (k + 1 >= out.length || out[k + 1]!.entry.char !== out[i]!.entry.char),
    );
    if (j > i) [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/**
 * The island Challenge: a strict set test that also reaches back. Every character of the set
 * gets two questions; then the weakest of the earlier characters (other sets of the lesson and,
 * when the caller merges their bundles in, earlier lessons) get one each, until the set is about
 * `CHALLENGE.setShare` of the whole. It is never shorter than `CHALLENGE.minQuestions`: when the
 * earlier pool can't fill it, the set gets a third question per character instead. Leaving an
 * island means showing the old words are still there too.
 */
export function buildChallenge(
  ctx: BuildContext,
  entries: CharacterEntry[],
  earlier: CharacterEntry[],
  seedTag = 'challenge',
): Question[] {
  if (entries.length === 0) return [];
  const rng = mulberry32(`${ctx.seed}:${seedTag}:pick`);
  let own = buildTest(ctx, entries, SESSION.testPerCharacterSet, seedTag);
  const wanted = Math.round((own.length * (1 - CHALLENGE.setShare)) / CHALLENGE.setShare);
  const setChars = new Set(entries.map((e) => e.char));
  const pool = earlier.filter((e) => !setChars.has(e.char));
  // Weakest first: the lowest mean score across the skills we track, with jitter so repeats vary.
  const weakness = (e: CharacterEntry) => {
    let sum = 0;
    let n = 0;
    for (const m of ctx.mastery.values())
      if (m.character === e.char) {
        sum += m.score;
        n++;
      }
    return (n ? sum / n : 50) + rng() * 15;
  };
  const chosen = pool
    .map((e) => ({ e, w: weakness(e) }))
    .sort((a, b) => a.w - b.w)
    .slice(0, wanted)
    .map((x) => x.e);
  const back = buildTest(ctx, chosen, 1, `${seedTag}:earlier`);
  if (own.length + back.length < CHALLENGE.minQuestions)
    own = buildTest(ctx, entries, SESSION.testPerCharacterSet + 1, seedTag);
  return spreadChars(shuffle([...own, ...back], mulberry32(`${ctx.seed}:${seedTag}:mix`)));
}

export function buildReview(ctx: BuildContext, now: Date): Question[] {
  const all = lessonEntries(ctx.bundle.lesson, ctx.higherChinese);
  const rng = mulberry32(`${ctx.seed}:review-select`);
  const chosen = new Set(
    selectReviewCharacters(
      all.map((e) => {
        const syllable = syllableInWord(e.char, wordOf(ctx, e), ctx.bundle.characters[e.char]);
        return {
          character: e.char,
          isWriteTarget: isWriteTarget(e, ctx.higherChinese),
          ...(syllable ? { tone: toneOf(syllable) } : {}),
        };
      }),
      ctx.mastery,
      now,
      SESSION.reviewQuestions,
      rng,
      ctx.weakTones ? { weakTones: ctx.weakTones } : undefined,
    ),
  );
  const entries = all.filter((e) => chosen.has(e.char));
  return composeMixed(ctx, entries.length ? entries : all, SESSION.reviewQuestions, 'review', true);
}

export function buildMistakes(ctx: BuildContext, chars: readonly string[]): Question[] {
  const set = new Set(chars);
  const entries = lessonEntries(ctx.bundle.lesson, ctx.higherChinese).filter((e) =>
    set.has(e.char),
  );
  return composeMixed(
    ctx,
    entries,
    Math.min(SESSION.practiceQuestions, entries.length * 2),
    'mistakes',
    false,
  );
}

export function buildModule(
  ctx: BuildContext,
  module: Module,
  setId: string | null,
  now = new Date(),
): Question[] {
  const entries = lessonEntries(ctx.bundle.lesson, ctx.higherChinese, setId ?? undefined);
  switch (module) {
    case 'learn':
      return buildLearn(ctx, entries);
    case 'listen':
      return buildListen(ctx, entries);
    case 'speak':
      return buildSpeak(ctx, entries);
    case 'read':
      return buildRead(ctx, entries);
    case 'write':
      return buildWrite(ctx, entries);
    case 'pinyin':
      return buildPinyin(ctx, entries);
    case 'practice':
      return buildPractice(ctx, entries);
    case 'game':
      return composeMixed(ctx, entries, SESSION.practiceQuestions, 'game', false);
    case 'test':
      return buildTest(
        ctx,
        entries,
        setId ? SESSION.testPerCharacterSet : SESSION.testPerCharacterLesson,
      );
    case 'review':
      return buildReview(ctx, now);
    case 'mistakes':
    case 'story':
      return []; // built elsewhere: mistakes from a character list, stories by the story player
    case 'word_meet':
    case 'word_listen':
    case 'word_find':
    case 'word_use':
    case 'word_write':
      return []; // built by buildWordGame: a word game needs the entry, not just the set
    // A games island's stop. The format travels in the route, and the runner builds it with
    // `buildGameRound`; this is the plain drill the slot falls back to when the set has no
    // playable game for it (no listening game builds, or the lesson band has not opened one).
    case 'island_meet':
      return buildLearn(ctx, entries);
    case 'island_listen':
      return buildListen(ctx, entries);
    case 'island_read':
      return buildRead(ctx, entries);
    case 'island_pinyin':
      return buildPinyin(ctx, entries);
    case 'island_write':
      return buildWrite(ctx, entries);
    case 'island_use':
      return buildPractice(ctx, entries);
    case 'challenge':
      // built elsewhere: once its lessons are merged into one bundle, by buildDailyChallenge
      // (dailyChallenge.ts)
      return [];
    case 'reading_practice':
      // built elsewhere: the practice player composes its own round from Passage/
      // PracticeQuestion content, not from CharacterEntry-shaped questions.
      return [];
  }
}

// ---------- word games (the word-first adventure islands) ----------

/**
 * The games one word gets, in order. Every word can be met, heard, found and used (each recipe
 * falls back to hanzi questions for abstract words); only write targets get the Write game.
 */
export function wordGames(e: CharacterEntry, higherChinese: boolean): WordGame[] {
  if (!e.wordId) return [];
  const games: WordGame[] = ['meet', 'listen', 'find', 'use'];
  if (isWriteTarget(e, higherChinese)) games.push('write');
  return games;
}

/**
 * One short game on one word: every question targets the same word, seen from a different
 * angle each time, with the choices ramping up as the game goes on. Nothing alternates
 * characters here (unlike `rounds`): the point is to sit with one word until it sticks.
 */
export function buildWordGame(ctx: BuildContext, e: CharacterEntry, game: WordGame): Question[] {
  const rng = mulberry32(`${ctx.seed}:word:${e.id}:${game}`);
  const q = (type: ExerciseType, opts: MakeOptions = {}) =>
    makeWithFallback(ctx, rng, type, e, opts);
  const plain = (type: ExerciseType, opts: MakeOptions = {}) =>
    makeQuestion(ctx, rng, type, e, opts);
  switch (game) {
    case 'meet':
      return compact([
        plain('introduce'),
        q('audio_to_picture', { choices: 2 }),
        q('hanzi_to_picture', { choices: 3 }),
        plain('speak'),
      ]);
    case 'listen':
      return compact([
        q('listen_catch', { catch: { length: 8, hits: 3 } }),
        q('audio_to_picture', { choices: 2 }),
        q('audio_to_hanzi', { choices: 3 }),
        q('audio_to_picture', { choices: 3 }),
        q('audio_to_hanzi', { choices: 4 }),
      ]);
    case 'find':
      return compact([
        q('find_all', { grid: { size: 9, targets: 3 } }),
        q('picture_to_hanzi', { choices: 2 }),
        q('find_all', { grid: { size: 12, targets: 4 } }),
        q('picture_to_hanzi', { choices: 3 }),
        q('tone'),
        q('pinyin_to_hanzi', { choices: 3 }),
      ]);
    case 'use':
      // No sentences here: with the word's picture on screen a fill-the-gap was picture
      // matching with extra steps, and P1 children found the sentence itself too much. The
      // Read stop and the tests still ask sentences.
      return compact([
        plain('speak'),
        q('picture_to_hanzi', { choices: 3 }),
        plain('speak'),
        q('match_pairs'),
        q('picture_to_hanzi', { choices: 4 }),
      ]);
    case 'write':
      if (!isWriteTarget(e, ctx.higherChinese)) return [];
      return compact([
        plain('trace', { trace: 'guided' }),
        plain('trace', { trace: 'faded' }),
        plain('trace', { trace: 'memory' }),
        plain('trace', { trace: 'memory' }),
      ]);
  }
}

/**
 * How many options a question puts in front of the child, or `null` when it is not a pick-one
 * board at all (tracing, speaking, a grid to sweep, a tray to drag).
 *
 * `maxTriesFor` uses it to decide whether a wrong answer earns another go. On a two-way choice it
 * does not: the child eliminates one and the second tap is right by arithmetic, so the "retry"
 * hands over a correct answer that was never retrieved — and the record counts it as one.
 */
export function optionCount(q: Question): number | null {
  switch (q.type) {
    case 'audio_to_picture':
    case 'hanzi_to_picture':
    case 'picture_to_hanzi':
    case 'audio_to_hanzi':
    case 'pinyin_to_hanzi':
    case 'tone':
    case 'sentence_fill':
      return q.choices.length;
    case 'listen_forks':
      return q.forks[q.step]?.choices.length ?? null;
    case 'listen_feed':
      return q.rounds[q.step]?.choices.length ?? null;
    case 'listen_sort':
      return q.baskets.length;
    default:
      return null;
  }
}

/** A two-way choice (see `optionCount` above) never earns a second try; a test doesn't either. */
const TWO_WAY_CHOICES = 2;

/**
 * How many tries a question gets before its answer is revealed and the session moves on to the
 * next one: one in strict (test) mode, one on a two-way choice for the reason `optionCount`
 * documents, two otherwise. Decided once here so the session store (which records the result) and
 * the runner (whose UI offers the retry and reveals the answer) never disagree with each other.
 */
export function maxTriesFor(q: Question, strict: boolean): number {
  const n = optionCount(q);
  return strict || (n !== null && n <= TWO_WAY_CHOICES) ? 1 : 2;
}

const REASK_SUFFIX = /:r\d+$/;

/** True for a question that is a re-ask of one the child got wrong earlier in the session. */
export const isReask = (id: string) => REASK_SUFFIX.test(id);

/**
 * The same question again, for the end of the session: same type and word, fresh distractors
 * from a new seed. The id keeps the original's, suffixed `:r<n>`, so it stays unique even after
 * a reload (the plain `qid` counter starts from zero again).
 */
/** Question types built over a whole set (games.ts), which makeQuestion cannot rebuild. */
const SET_TYPES: ReadonlySet<ExerciseType> = new Set([
  'introduce_trio',
  'listen_forks',
  'listen_feed',
  'listen_scene',
  'listen_build',
  'listen_bubbles',
  'listen_sort',
]);

export function reaskQuestion(ctx: BuildContext, q: Question, n: number): Question | null {
  const reaskId = `${q.id.replace(REASK_SUFFIX, '')}:r${n}`;
  // A set-built board is asked again exactly as it was: same tray, same fork, same shell.
  if (SET_TYPES.has(q.type)) return { ...q, id: reaskId };
  const opts: MakeOptions = {};
  if ('choices' in q && q.type !== 'tone') opts.choices = q.choices.length;
  if (q.type === 'trace') opts.trace = q.mode;
  if (q.type === 'find_all')
    opts.grid = { size: q.tiles.length, targets: q.targetCount, prompt: q.prompt };
  if (q.type === 'listen_catch')
    opts.catch = {
      length: q.sequence.length,
      hits: q.sequence.filter((w) => w.id === q.answerId).length,
    };
  if (q.type === 'stroke_missing') opts.strokeIdx = q.strokeIdx;
  if (q.type === 'match_pairs') opts.pairs = q.pairs.length;
  if (q.type === 'tone_draw') opts.toneMode = q.mode;
  const rng = mulberry32(`${ctx.seed}:reask:${q.id}:${n}`);
  const again = makeQuestion(ctx, rng, q.type, q.entry, opts);
  if (!again) return null;
  // A framed question is re-asked with the same frame data (the board looks the same).
  return {
    ...again,
    ...(q.frame ? { frame: q.frame } : {}),
    id: reaskId,
  };
}
